"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Terms() {
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: barRef,
    isVisible: barVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: signatureRef,
    isVisible: signatureVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4124" id="terms-of-service" className="relative bg-background text-foreground min-h-screen py-[200px] px-6 md:px-12 lg:px-20 overflow-hidden">
      {/* Noise texture overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-[1400px]">
        {/* Eyebrow label */}
        <div ref={labelRef} className={`flex justify-center mb-16 md:mb-24 transition-all duration-700 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}>
          <span className="font-mono text-[14px] tracking-[0.3em] uppercase" style={{
          color: "var(--primary)"
        }}>
            TERMS OF SERVICE &mdash; 02 / AGREEMENT
          </span>
        </div>

        {/* Hero headline */}
        <h1 ref={headlineRef} className={`font-default font-bold uppercase leading-[0.92] tracking-[-0.04em] text-left mb-20 md:mb-28 transition-all duration-1000 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        fontSize: "clamp(48px, 11vw, 180px)",
        width: "85%"
      }}>
          <span className="block text-foreground">THE RULES OF</span>
          <span className="block" style={{
          color: "var(--primary)"
        }}>
            ENGAGEMENT.
          </span>
        </h1>

        {/* Accent bar */}
        <div ref={barRef} className={`mb-20 md:mb-28 transition-all duration-700 ease-out ${barVisible ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"}`} style={{
        transformOrigin: "top"
      }}>
          <div className="w-[4px] h-[80px]" style={{
          backgroundColor: "var(--primary)"
        }} />
        </div>

        {/* Document grid: Sticky Sidebar + 11 Numbered Sections */}
        <div ref={bodyRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-24 md:mb-32 transition-all duration-1000 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Sticky sidebar */}
          <div className="lg:col-span-4 lg:col-start-1">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-default font-bold uppercase text-foreground tracking-[-0.02em] leading-[1] text-[28px] md:text-[32px]">
                TERMS OF
                <br />
                SERVICE
              </h2>
              <div className="mt-6 h-[2px] w-16" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>
          </div>

          {/* Sequential document column */}
          <div className="lg:col-span-7 lg:col-start-6">
            <div className="space-y-14 md:space-y-16">
              {/* 1. Agreeing to these terms */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  1. Agreeing to these terms
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    By using Resseti (or accessing our platform, services, or website), you agree to these Terms of Service. If you are using our services on behalf of an educational institution, school, or organization, you confirm that you have the authority to bind that entity to these terms.
                  </p>
                  <p>
                    If you do not agree to these terms, do not use our services.
                  </p>
                </div>
              </article>

              {/* 2. Our services */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  2. Our services
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    Resseti provides diagnostic educational assessment infrastructure, mark-scheme logic analysis, and process-error identification tools. We reserve the right to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 marker:text-[var(--primary)]">
                    <li>Modify, update, or discontinue features of our services at any time;</li>
                    <li>Implement security measures, diagnostics, or technical improvements;</li>
                    <li>Restrict or terminate access if these terms are breached or if misuse is detected.</li>
                  </ul>
                </div>
              </article>

              {/* 3. Accounts and access */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  3. Accounts and access
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    You must ensure all account credentials, authentication keys, and access permissions are kept secure. You are responsible for all activity conducted through your account or organizational seats.
                  </p>
                  <p>
                    You agree to notify us immediately of any unauthorized access or security breach.
                  </p>
                </div>
              </article>

              {/* 4. Acceptable use */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  4. Acceptable use
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    You agree not to reverse engineer, decompile, or attempt to extract the source code or diagnostic mark-scheme models of Resseti; interfere with, disrupt, or place an unreasonable load on our infrastructure or networks; use the service to transmit malicious code, malware, or unlawful content; or resell, sublicense, or redistribute our services without prior written agreement.
                  </p>
                </div>
              </article>

              {/* 5. Intellectual property */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  5. Intellectual property
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    All intellectual property rights in Resseti, our diagnostic engine (Lumina), mark-scheme mapping algorithms, software, documentation, and branding belong to [Resseti Ltd] or our licensors.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 marker:text-[var(--primary)]">
                    <li>You retain all ownership rights to student work, raw assessment files, and proprietary educational content uploaded to the platform;</li>
                    <li>You grant us a limited, worldwide, non-exclusive license to process and analyze such data solely to deliver, improve, and validate the diagnostic services under strict confidentiality and privacy agreements.</li>
                  </ul>
                </div>
              </article>

              {/* 6. Fees and payment */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  6. Fees and payment
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    Subscription fees, pilot terms, or institutional service fees are agreed upon in our service schedules, order forms, or pilot agreements. All fees are non-refundable unless explicitly agreed otherwise in writing.
                  </p>
                  <p>
                    Unpaid balances may result in suspension or termination of access.
                  </p>
                </div>
              </article>

              {/* 7. Privacy and student data */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  7. Privacy and student data
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    We handle personal data and student information in strict compliance with applicable data protection laws, including UK GDPR and the Data Protection Act 2018, as detailed in our Privacy Policy.
                  </p>
                  <p>
                    We do not sell student data, track students for commercial advertising, or profile learners for third-party commercial exploitation.
                  </p>
                </div>
              </article>

              {/* 8. Disclaimers and warranties */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  8. Disclaimers and warranties
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    Our services are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. While we design our diagnostic engine to meet rigorous academic mark-scheme standards, we do not guarantee that diagnostic insights will be error-free or uninterrupted.
                  </p>
                  <p>
                    Any pedagogical marks or error classifications are provided for formative diagnostic guidance.
                  </p>
                </div>
              </article>

              {/* 9. Limitation of liability */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  9. Limitation of liability
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>To the maximum extent permitted by law, Resseti Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or loss of data, profits, or educational accreditation.</p>
                  <p>Our total liability for any claim arising out of these terms shall not exceed the amount paid by you to Resseti Ltd during the twelve (12) months preceding the claim or a fixed amount, e.g. £1,000, if higher.</p>
                </div>
              </article>

              {/* 10. Termination */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  10. Termination
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    We may suspend or terminate your access immediately if you breach these terms.
                  </p>
                  <p>You may terminate your agreement in accordance with the terms of your specific service schedule or by written notice to hello@resseti.com. Upon termination, all licenses granted to you terminate, and access to the platform will cease.</p>
                </div>
              </article>

              {/* 11. General */}
              <article className="space-y-4">
                <h3 className="font-default font-semibold text-xl md:text-2xl tracking-tight" style={{
                color: "var(--primary)"
              }}>
                  11. General
                </h3>
                <div className="space-y-3 font-default text-foreground/90 text-base md:text-lg leading-relaxed">
                  <p>
                    These terms constitute the entire agreement between you and [Resseti Ltd] regarding our services. If any provision is deemed unenforceable, the remaining provisions remain in full force.
                  </p>
                  <p>
                    These terms are governed by the laws of England and Wales, and any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                  </p>
                  <p>
                    Questions regarding these terms should be directed to [hello@resseti.com] or by post to [Resseti Ltd], [address].
                  </p>
                  <p className="font-mono text-sm text-muted-foreground pt-2">Last updated: 27.09.2026</p>
                </div>
              </article>
            </div>
          </div>
        </div>

        {/* Signature line & contact footer */}
        <div ref={signatureRef} className={`mt-24 md:mt-32 transition-all duration-1000 ease-out ${signatureVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="space-y-3 mb-8">
            <p className="font-mono uppercase tracking-[0.25em]" style={{
            fontSize: "12px",
            color: "var(--primary)"
          }}>
              SIGNED &mdash; THE RESSETI TEAM
            </p>
            <p className="font-mono text-xs tracking-wider" style={{
            color: "var(--muted-foreground)"
          }}>
              Questions regarding these terms should be directed to [hello@resseti.com] or by post to [Resseti Ltd], [address].
            </p>
          </div>

          {/* Glint line */}
          <div className="relative h-[1px] w-full overflow-hidden bg-border">
            <div className={`absolute inset-y-0 left-0 h-[1px] transition-transform duration-[1500ms] ease-out ${signatureVisible ? "scale-x-100" : "scale-x-0"}`} style={{
            backgroundColor: "var(--primary)",
            width: "100%",
            transformOrigin: "left",
            boxShadow: "0 0 12px var(--primary)"
          }} />
          </div>
        </div>
      </div>
    </section>;
}